'use client'

import React, { useRef, useEffect, useState, useMemo, useCallback } from 'react'
import { Shard } from './Shard'
import ReactMemo from 'react'
import { getPseudoRandomShardList, getRandomShard } from './Pixelator/shards'
import { cn } from '@/utilities/ui'
import { debounce } from 'lodash'

// Types for grid and shard placement
interface CamoShardAreaProps {
  className?: string
  cellSize?: number
  regionSize?: number // Size of region in camo pixels; one shard per region.
  shardPadding?: number // Minimum camo-pixel padding around each 3x3 shard block.
  ignoreRegions?: Array<{ rowStart: number; rowEnd: number; colStart: number; colEnd: number }>
  bleed?: number // Extra camo pixels to render outside container on each side.
  ignorePadding?: number // Extra padding around ignore regions when excluding shards (in camo pixels).
  enableRandomUpdates?: boolean
}

const camoColorsAndFrequencies = [
  ['#26320C', 3],
  ['#1D2609', 3],
  ['#192108', 3],
  ['#131A06', 1],
]
const randomCamoColorList = camoColorsAndFrequencies.flatMap(([color, frequency]) =>
  Array(frequency).fill(color),
)
// Utility to generate a camo color (more natural, less saturated)
function getCamoColor(): string {
  return randomCamoColorList[Math.floor(Math.random() * randomCamoColorList.length)] as string
}

// Generate shard positions: one per region.
function generateShardPositionsForRegions(
  totalRows: number,
  totalCols: number,
  regionSize: number,
  padding: number,
  ignoreRegions: Array<{ rowStart: number; rowEnd: number; colStart: number; colEnd: number }> = [],
  bleed = 0,
): Array<{ row: number; col: number }> {
  const positions: Array<{ row: number; col: number }> = []
  if (regionSize < 3 + padding * 2) return positions // Regions too small

  const regionRows = Math.floor(totalRows / regionSize)
  const regionCols = Math.floor(totalCols / regionSize)

  for (let r = 0; r < regionRows; r++) {
    for (let c = 0; c < regionCols; c++) {
      const regionStartRow = r * regionSize
      const regionStartCol = c * regionSize

      const rowMin = regionStartRow + padding
      const rowMax = regionStartRow + regionSize - 3 - padding
      const colMin = regionStartCol + padding
      const colMax = regionStartCol + regionSize - 3 - padding

      if (rowMax < rowMin || colMax < colMin) {
        // Region too small for shard with padding
        continue
      }

      const row = rowMin + Math.floor(Math.random() * (rowMax - rowMin + 1))
      const col = colMin + Math.floor(Math.random() * (colMax - colMin + 1))

      const shardArea = {
        rowStart: row,
        rowEnd: row + 2,
        colStart: col,
        colEnd: col + 2,
      }

      const overlapsIgnore = ignoreRegions.some((reg) => rectsOverlap(shardArea, reg))

      const withinVisible =
        row >= bleed && col >= bleed && row <= totalRows - bleed - 3 && col <= totalCols - bleed - 3

      if (!overlapsIgnore && withinVisible) {
        positions.push({ row, col })
      }
    }
  }

  return positions
}

function rectsOverlap(
  a: { rowStart: number; rowEnd: number; colStart: number; colEnd: number },
  b: { rowStart: number; rowEnd: number; colStart: number; colEnd: number },
): boolean {
  return (
    a.rowStart <= b.rowEnd &&
    a.rowEnd >= b.rowStart &&
    a.colStart <= b.colEnd &&
    a.colEnd >= b.colStart
  )
}

const CamoGrid = React.memo(function CamoGrid({
  rows,
  cols,
  cellSize,
  bleed,
}: {
  rows: number
  cols: number
  cellSize: number
  bleed: number
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const totalRows = rows + bleed * 2
    const totalCols = cols + bleed * 2

    canvas.width = totalCols * cellSize
    canvas.height = totalRows * cellSize

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    for (let y = 0; y < totalRows; y++) {
      for (let x = 0; x < totalCols; x++) {
        const color = getCamoColor()
        ctx.fillStyle = color
        ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize)
      }
    }
  }, [rows, cols, cellSize, bleed])

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0"
      style={{
        top: -bleed * cellSize,
        left: -bleed * cellSize,
        width: (cols + bleed * 2) * cellSize,
        height: (rows + bleed * 2) * cellSize,
      }}
      aria-hidden="true"
    />
  )
})

// -----------------------------------------------------------------------------

const randomUpdateInterval = 3000
const clickAnimShrinkMs = 60
const clickAnimGrowMs = 100

const MemoShard = ReactMemo.memo(Shard)

interface ShardCellProps {
  shard: ReturnType<typeof getRandomShard>
  pos: { row: number; col: number }
  index: number
  animating: boolean
  newPhase: boolean
  click: () => void
  cellSize: number
  bleed: number
}

const ShardCell = ReactMemo.memo(
  function ShardCell({ shard, pos, animating, newPhase, click, cellSize, bleed }: ShardCellProps) {
    return (
      <div
        className={cn(
          'absolute z-10 flex items-center justify-center transition-all duration-200 cursor-pointer',
          animating && (newPhase ? 'scale-150' : 'scale-0 opacity-0'),
        )}
        style={{
          top: (pos.row - bleed) * cellSize,
          left: (pos.col - bleed) * cellSize,
          width: cellSize * 3,
          height: cellSize * 3,
          pointerEvents: 'auto',
        }}
        onMouseDown={click}
      >
        <MemoShard shard={shard} size={cellSize * 3} align="start" force3x3 />
      </div>
    )
  },
  (prev, next) =>
    prev.shard === next.shard &&
    prev.animating === next.animating &&
    prev.newPhase === next.newPhase,
)

export function ShardArea({
  className,
  cellSize = 10,
  regionSize = 20,
  shardPadding = 4,
  ignoreRegions = [],
  bleed = 50,
  ignorePadding = 2,
  enableRandomUpdates = true,
}: CamoShardAreaProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [dimensions, setDimensions] = useState<{ rows: number; cols: number } | null>(null)
  const lastPlacementDims = useRef<{ rows: number; cols: number } | null>(null)

  const recalcGridSize = useCallback(() => {
    const node = containerRef.current
    if (!node) return
    const { clientWidth, clientHeight } = node
    const cols = Math.max(1, Math.ceil(clientWidth / cellSize))
    const rows = Math.max(1, Math.ceil(clientHeight / cellSize))
    setDimensions((prev) => {
      if (prev && prev.rows === rows && prev.cols === cols) return prev
      return { rows, cols }
    })
  }, [cellSize])

  useEffect(() => {
    recalcGridSize() // Initial measure
    const ro = new ResizeObserver(recalcGridSize)
    if (containerRef.current) ro.observe(containerRef.current)
    return () => ro.disconnect()
  }, [recalcGridSize])

  const extended = useMemo(() => {
    if (!dimensions) return { rows: 0, cols: 0 }
    return { rows: dimensions.rows + bleed * 2, cols: dimensions.cols + bleed * 2 }
  }, [dimensions, bleed])

  // Memoize random shard positions and shards
  const [shardPositions, setShardPositions] = useState<Array<{ row: number; col: number }>>([])
  const [shardList, setShardList] = useState(() => getPseudoRandomShardList(0))
  const [animatingShardIndex, setAnimatingShardIndex] = useState<number | null>(null)
  const [isNewShard, setIsNewShard] = useState(false)

  useEffect(() => {
    if (!dimensions) return
    if (
      lastPlacementDims.current &&
      lastPlacementDims.current.rows === extended.rows &&
      lastPlacementDims.current.cols === extended.cols
    ) {
      return // Already placed for these dims (avoids StrictMode double run)
    }
    // Shift ignore regions by bleed so they align with extended grid
    const shiftedIgnores = ignoreRegions.map((reg) => ({
      rowStart: reg.rowStart + bleed - ignorePadding,
      rowEnd: reg.rowEnd + bleed + ignorePadding,
      colStart: reg.colStart + bleed - ignorePadding,
      colEnd: reg.colEnd + bleed + ignorePadding,
    }))

    const positions = generateShardPositionsForRegions(
      extended.rows,
      extended.cols,
      regionSize,
      shardPadding,
      shiftedIgnores,
      bleed,
    )
    setShardPositions(positions)
    setShardList(getPseudoRandomShardList(positions.length))
    lastPlacementDims.current = { rows: extended.rows, cols: extended.cols }
  }, [extended, dimensions, regionSize, shardPadding, ignoreRegions, bleed, ignorePadding])

  // Animate and update a specific shard by index
  const animateAndUpdateShard = useCallback((index: number) => {
    setAnimatingShardIndex(index)
    setIsNewShard(false) // shrink

    setTimeout(() => {
      // swap shard after shrink
      setShardList((current) => {
        const next = [...current]
        next[index] = getRandomShard()
        return next
      })
      setIsNewShard(true) // grow

      setTimeout(() => setAnimatingShardIndex(null), clickAnimGrowMs)
    }, clickAnimShrinkMs)
  }, [])

  // Random shard update
  const updateRandomShard = useCallback(() => {
    setShardList((current) => {
      if (current.length === 0) return current
      const randomIndex = Math.floor(Math.random() * current.length)
      setAnimatingShardIndex(randomIndex)
      setIsNewShard(false)

      setTimeout(() => {
        const next = [...current]
        next[randomIndex] = getRandomShard()
        setShardList(next)
        setIsNewShard(true)
        setTimeout(() => setAnimatingShardIndex(null), clickAnimGrowMs)
      }, clickAnimShrinkMs)
      return current
    })
  }, [])

  // Interval effect
  useEffect(() => {
    if (!enableRandomUpdates || shardList.length === 0) return
    const id = setInterval(updateRandomShard, randomUpdateInterval)
    return () => clearInterval(id)
  }, [enableRandomUpdates, shardList.length, updateRandomShard])

  if (!dimensions) {
    return (
      <div ref={containerRef} className={className} style={{ width: '100%', height: '100%' }} />
    )
  }

  return (
    <div
      ref={containerRef}
      className={cn('relative w-full h-full overflow-hidden', className)}
      style={{
        minHeight: dimensions.rows * cellSize,
        minWidth: dimensions.cols * cellSize,
      }}
    >
      {/* Memoized camo background grid */}
      <CamoGrid rows={dimensions.rows} cols={dimensions.cols} cellSize={cellSize} bleed={bleed} />
      {/* Shards snapped to 3x3 camo grid blocks */}
      {shardPositions.map((pos, i) => (
        <ShardCell
          key={`${pos.row}-${pos.col}`}
          shard={shardList[i] ?? getRandomShard()}
          pos={pos}
          index={i}
          animating={animatingShardIndex === i}
          newPhase={isNewShard}
          click={() => {
            if (animatingShardIndex === i) return
            animateAndUpdateShard(i)
          }}
          cellSize={cellSize}
          bleed={bleed}
        />
      ))}
    </div>
  )
}
